"use client";
import { Eye, EyeOff } from "lucide-react";
import React from "react";

interface CInputProps {
  type?: string;
  id?: string;
  name?: string;
  label?: React.ReactNode;
  placeholder?: string;
  className?: string;
  value?: string | number | readonly string[] | undefined;
  defaultValue?: string | number | readonly string[] | undefined;
  errorQuery?: boolean;
  errorMessage?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  pattern?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  min?: number | string;
  max?: number | string;
  minLength?: number;
  maxLength?: number;
  step?: number;
  multiple?: boolean;
  accept?: string;
  endIcon?: React.ReactNode;
  width?: string;
  style?: React.CSSProperties;
  title?: string;
  role?: "client" | "admin";
}

const CInput: React.FC<CInputProps> = ({
  type = "text",
  title = "",
  id = "",
  name = "",
  label = "",
  placeholder,
  className = "",
  value,
  defaultValue,
  errorQuery = false,
  errorMessage = "Invalid input",
  onChange,
  onBlur,
  onKeyDown,
  disabled = false,
  endIcon,
  width,
  role,
  ...rest
}) => {
  // Get user role from localStorage if not provided
  // Get user role from localStorage if color prop is not provided
  let detectedRole = "admin";
  if (typeof window !== "undefined") {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userObj = JSON.parse(userStr);
        detectedRole = userObj?.user_type === "client" ? "client" : "admin";
      }
    } catch {}
  }
  // Only use string label for id
  const stringLabel = typeof label === "string" ? label : "";
  const inputId = id || name || stringLabel.toLowerCase().replace(/\s+/g, "-");
  const [showPassword, setShowPassword] = React.useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // inside CInput component, after state and togglePasswordVisibility
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    type PickerInput = HTMLInputElement & {
      showPicker?: () => void;
    };

    if (
      (type === "date" || type === "datetime-local" || type === "time") &&
      inputRef.current &&
      typeof (inputRef.current as PickerInput).showPicker === "function"
    ) {
      (inputRef.current as PickerInput).showPicker?.();
    }
  };

  // Determine focus border color based on role
  const focusBorderColor =
    detectedRole === "client"
      ? "focus:border-client-primary"
      : "focus:border-primary";

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="text-sm text-muted-foreground mb-1 block"
        >
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          title={title}
          type={type === "password" && showPassword ? "text" : type}
          placeholder={placeholder || stringLabel}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onClick={handleClick}
          className={`
            ${className}
            ${width || "w-full"}
            rounded-md px-3 py-2 outline-none
            placeholder:italic placeholder:text-sm placeholder:text-muted-foreground/60
            bg-transparent
            border
            ${errorQuery ? "border-red-500" : "border-border/50"}
            ${errorQuery ? "focus:border-red-500 focus:ring-1 focus:ring-red-500" : `${focusBorderColor} focus:ring-1 focus:ring-primary`}
            focus:outline-none
            ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-border"}
            ${type === "password" ? "pr-12" : endIcon ? "pr-12" : ""}
            transition-colors duration-200
          `}
          {...rest}
        />

        {type === "password" && (
          <div className="absolute inset-y-0 right-3 flex items-center z-10">
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="text-muted-foreground hover:text-foreground focus:outline-none"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        )}

        {endIcon && type !== "password" && (
          <div className="absolute inset-y-0 right-3 flex items-center text-muted-foreground z-10">
            {endIcon}
          </div>
        )}
      </div>

      {errorQuery && (
        <p className="text-red-500 text-xs mt-1">{errorMessage}</p>
      )}
    </div>
  );
};

export default CInput;
